<template>
  <DatePicker
    ref="vcDatePickerRef"
    v-bind="{ placeholder, minDate, maxDate, disabled }"
    :model-value="modelValue"
    :masks="{ L: 'MM/DD/YYYY' }"
    @update:model-value="onModelUpdate"
    :is-range="isRange"
    :is-custom-select="isCustomSelect"
    @popover-did-show="onPopoverDidShow"
    @popover-did-hide="onPopoverDidHide"
  >
    <template #default="{ inputValue, togglePopover }">
      <InputField
        :class="inputClass"
        ref="input"
        :id="inputId || undefined"
        :label-id="labelId || undefined"
        :model-value="
          !isRange
            ? inputValue
            : inputValue.start && inputValue.end
              ? inputValue.start + '-' + inputValue.end
              : null
        "
        @click="showCalendar(togglePopover, inputValue)"
        append-icon="ri-calendar-line"
        :readonly="true"
        :autocomplete="autocomplete"
        v-bind="{
          innerLabel,
          invalidFeedback,
          disabled,
          isInvalid,
          placeholder
        }"
      >
      </InputField>
    </template>
  </DatePicker>
</template>

<script>
import { watchEffect, ref, nextTick, onUnmounted } from "vue"
import { useI18n } from "vue-i18n"
import { DatePicker } from "v-calendar"
import InputField from "./InputField.vue"

const FOCUSABLE_SELECTOR = [
  "a[href]:not([tabindex='-1'])",
  "button:not([disabled]):not([tabindex='-1'])",
  "input:not([disabled]):not([tabindex='-1'])",
  "select:not([disabled]):not([tabindex='-1'])",
  "textarea:not([disabled]):not([tabindex='-1'])",
  "[tabindex]:not([tabindex='-1'])"
].join(", ")

const getFocusableIn = (root) => {
  if (!root?.querySelectorAll) return []
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      typeof el?.focus === "function" &&
      !el.hasAttribute("disabled") &&
      el.getAttribute("aria-hidden") !== "true"
  )
}

export default {
  components: { DatePicker, InputField },
  emits: ["update:modelValue"],
  props: {
    placeholder: String,
    minDate: [Date, String, Object],
    maxDate: [Date, String, Object],
    modelValue: [String, Date, Object],
    isInvalid: Boolean,
    invalidFeedback: [String, Array],
    disabled: Boolean,
    innerLabel: String,
    inputClass: String,
    /** Id for the text input (pairs with `<Label :html-for="inputId">`). */
    inputId: {
      type: String,
      default: null
    },
    /** Id of the visible `<label>` for `aria-labelledby` / `for`. */
    labelId: {
      type: String,
      default: null
    },
    /** HTML autocomplete; `off` avoids false `autocomplete` purpose issues on custom date fields. */
    autocomplete: {
      type: String,
      default: "off"
    },
    isRange: {
      type: Boolean,
      default: false
    },
    isCustomSelect: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const input = ref(null)
    const popoverPanelEl = ref(null)
    let removeTabTrap = null
    let navArrowAriaObserver = null

    const setVCalendarNavArrowsLabel = (root) => {
      if (!root?.querySelector) return
      const prev = t("common.a11y_datepicker_previous_month")
      const next = t("common.a11y_datepicker_next_month")
      root.querySelectorAll("button.vc-arrow.vc-prev").forEach((el) => {
        el.setAttribute("aria-label", prev)
      })
      root.querySelectorAll("button.vc-arrow.vc-next").forEach((el) => {
        el.setAttribute("aria-label", next)
      })
    }

    const stopNavArrowAriaObserver = () => {
      navArrowAriaObserver?.disconnect()
      navArrowAriaObserver = null
    }

    const onModelUpdate = (value) => {
      emit("update:modelValue", value)
    }
    const showCalendar = (togglePopover) => {
      return props.disabled ? null : togglePopover()
    }

    const vcDatePickerRef = ref(null)

    const onTabTrapKeydown = (e) => {
      if (e.key === "Escape" && popoverPanelEl.value) {
        e.preventDefault()
        e.stopPropagation()
        vcDatePickerRef.value?.hidePopover?.()
        return
      }
      if (e.key !== "Tab" || !popoverPanelEl.value) return
      const container = popoverPanelEl.value
      if (!container.isConnected) return

      const focusables = getFocusableIn(container)
      if (!focusables.length) {
        e.preventDefault()
        return
      }

      const active = document.activeElement
      if (!container.contains(active)) {
        e.preventDefault()
        focusables[0]?.focus()
        return
      }

      const idx = focusables.indexOf(active)
      if (e.shiftKey) {
        if (idx <= 0) {
          e.preventDefault()
          focusables[focusables.length - 1]?.focus()
        }
      } else if (idx === focusables.length - 1 || idx === -1) {
        e.preventDefault()
        focusables[0]?.focus()
      }
    }

    const stopTabTrap = () => {
      removeTabTrap?.()
      removeTabTrap = null
      popoverPanelEl.value = null
    }

    const onPopoverDidShow = (el) => {
      if (!el || props.disabled) return
      stopTabTrap()
      stopNavArrowAriaObserver()
      popoverPanelEl.value = el
      document.addEventListener("keydown", onTabTrapKeydown, true)
      removeTabTrap = () => {
        document.removeEventListener("keydown", onTabTrapKeydown, true)
      }

      nextTick(() => {
        requestAnimationFrame(() => {
          setVCalendarNavArrowsLabel(el)
          stopNavArrowAriaObserver()
          navArrowAriaObserver = new MutationObserver(() => {
            setVCalendarNavArrowsLabel(el)
          })
          navArrowAriaObserver.observe(el, { childList: true, subtree: true })

          const focusables = getFocusableIn(el)
          focusables[0]?.focus?.()
        })
      })
    }

    const onPopoverDidHide = () => {
      stopNavArrowAriaObserver()
      stopTabTrap()
    }

    watchEffect(() => {
      if (props.isCustomSelect && input.value?.$el) {
        nextTick(() => {
          input.value.$el.click()
        })
      }
    })

    onUnmounted(() => {
      stopNavArrowAriaObserver()
      stopTabTrap()
    })

    return {
      input,
      vcDatePickerRef,
      onModelUpdate,
      showCalendar,
      onPopoverDidShow,
      onPopoverDidHide
    }
  }
}
</script>

<style lang="scss">
.vc-popover-content-wrapper {
  --custom-date-picker-focus-color: var(--focus-color, #002e5c);
  --custom-date-picker-focus-contrast: var(--focus-contrast-color, #fff);

  .vc-arrow:focus-visible,
  .vc-nav-arrow:focus-visible {
    outline: 2px solid var(--custom-date-picker-focus-color);
    outline-offset: 2px;
    box-shadow:
      0 0 0 2px var(--custom-date-picker-focus-contrast),
      0 0 0 4px var(--custom-date-picker-focus-color);
    border-radius: 4px;
  }
}
</style>
